import type { PixelIconName } from '@/components/ui/PixelIcon';
import type { WorldEnemyType } from '@/types/enemy';

export type WorldId =
  | 'greenwood'
  | 'stonehollow'
  | 'frostline'
  | 'emberdeep'
  | 'arcana';

export interface WorldDefinition {
  id: WorldId;
  name: string;
  subtitle: string;
  icon: PixelIconName;
  className: string;
  enemyType: WorldEnemyType;
  enemySetName: string;
  enemies: WorldEnemyDefinition[];
  visual: WorldVisualTheme;
  // Used for card backgrounds (kept as CSS strings so we can tune without new assets).
  gradient: string;
  // Inclusive range for the world.
  levelStart: number;
  levelEnd: number;
}

export interface WorldEnemyDefinition {
  id: string;
  name: string;
  role: string;
  icon: PixelIconName;
  accent: string;
}

export interface WorldVisualTheme {
  surface: string;
  edge: string;
  glow: string;
}

export const WORLDS: WorldDefinition[] = [
  {
    id: 'greenwood',
    name: 'World 1: Greenwood',
    subtitle: 'Gentle puzzles, crisp wins.',
    icon: 'home',
    className: 'world-theme-greenwood',
    enemyType: 'bramble_beetle',
    enemySetName: 'Bramble Pack',
    enemies: [
      { id: 'bramble-beetle', name: 'Bramble Beetle', role: 'Dashes in clean lines.', icon: 'mask', accent: '#7ed957' },
      { id: 'dew-slime', name: 'Dew Slime', role: 'Splits when cornered.', icon: 'eye', accent: '#4ecdc4' },
      { id: 'twig-runner', name: 'Twig Runner', role: 'Peeks then sprints.', icon: 'speedBoost', accent: '#95d84e' },
    ],
    visual: {
      surface: 'rgba(64, 174, 112, 0.22)',
      edge: '#2f8f63',
      glow: 'rgba(110, 231, 183, 0.45)',
    },
    gradient: 'linear-gradient(135deg, rgba(46, 204, 113, 0.18) 0%, rgba(78, 205, 196, 0.16) 55%, rgba(52, 152, 219, 0.12) 100%)',
    levelStart: 0,
    levelEnd: 13,
  },
  {
    id: 'stonehollow',
    name: 'World 2: Stonehollow',
    subtitle: 'Tighter corridors. Sharper reads.',
    icon: 'levels',
    className: 'world-theme-stonehollow',
    enemyType: 'tunnel_drone',
    enemySetName: 'Quarry Ring',
    enemies: [
      { id: 'quarry-guard', name: 'Quarry Guard', role: 'Blocks narrow lanes.', icon: 'shield', accent: '#b7c0cb' },
      { id: 'shard-bat', name: 'Shard Bat', role: 'Cuts across corners.', icon: 'retry', accent: '#8ca6bf' },
      { id: 'tunnel-drone', name: 'Tunnel Drone', role: 'Tracks your last move.', icon: 'gamepad', accent: '#93a4b2' },
    ],
    visual: {
      surface: 'rgba(91, 108, 125, 0.24)',
      edge: '#5f6d7a',
      glow: 'rgba(176, 190, 208, 0.42)',
    },
    gradient: 'linear-gradient(135deg, rgba(236, 240, 241, 0.14) 0%, rgba(149, 165, 166, 0.12) 55%, rgba(52, 73, 94, 0.18) 100%)',
    levelStart: 14,
    levelEnd: 27,
  },
  {
    id: 'frostline',
    name: 'World 3: Frostline',
    subtitle: 'Stops, slides, and clean routes.',
    icon: 'levels',
    className: 'world-theme-frostline',
    enemyType: 'skid_mite',
    enemySetName: 'Rime Patrol',
    enemies: [
      { id: 'rime-wisp', name: 'Rime Wisp', role: 'Leaves slick trails.', icon: 'effects', accent: '#9de7ff' },
      { id: 'skid-mite', name: 'Skid Mite', role: 'Bounces off stop tiles.', icon: 'speedBoost', accent: '#7fd4f2' },
      { id: 'glacier-eye', name: 'Glacier Eye', role: 'Locks onto exits.', icon: 'eye', accent: '#b7f0ff' },
    ],
    visual: {
      surface: 'rgba(77, 192, 225, 0.2)',
      edge: '#3f99bb',
      glow: 'rgba(140, 222, 255, 0.44)',
    },
    gradient: 'linear-gradient(135deg, rgba(224, 247, 250, 0.16) 0%, rgba(77, 208, 225, 0.12) 55%, rgba(52, 152, 219, 0.12) 100%)',
    levelStart: 28,
    levelEnd: 41,
  },
  {
    id: 'emberdeep',
    name: 'World 4: Emberdeep',
    subtitle: 'Heat, risk, and recovery.',
    icon: 'fire',
    className: 'world-theme-emberdeep',
    enemyType: 'cinder_hound',
    enemySetName: 'Cinder Syndicate',
    enemies: [
      { id: 'cinder-hound', name: 'Cinder Hound', role: 'Rushes lava lanes.', icon: 'fire', accent: '#ff8b4d' },
      { id: 'magma-shell', name: 'Magma Shell', role: 'Holds hot choke points.', icon: 'shield', accent: '#ffb35e' },
      { id: 'ash-sentinel', name: 'Ash Sentinel', role: 'Punishes long routes.', icon: 'target', accent: '#ff9a62' },
    ],
    visual: {
      surface: 'rgba(226, 114, 60, 0.24)',
      edge: '#c95c2e',
      glow: 'rgba(255, 149, 94, 0.45)',
    },
    gradient: 'linear-gradient(135deg, rgba(243, 156, 18, 0.16) 0%, rgba(231, 76, 60, 0.14) 55%, rgba(44, 62, 80, 0.16) 100%)',
    levelStart: 42,
    levelEnd: 53,
  },
  {
    id: 'arcana',
    name: 'World 5: Arcana',
    subtitle: 'Portals, loops, and strange geometry.',
    icon: 'portal',
    className: 'world-theme-arcana',
    enemyType: 'echo_mirror',
    enemySetName: 'Loop Court',
    enemies: [
      { id: 'warp-specter', name: 'Warp Specter', role: 'Teleports behind you.', icon: 'portal', accent: '#c08bff' },
      { id: 'rune-golem', name: 'Rune Golem', role: 'Anchors portal hubs.', icon: 'crown', accent: '#9dc4ff' },
      { id: 'echo-mirror', name: 'Echo Mirror', role: 'Copies your direction.', icon: 'mask', accent: '#7fd8ff' },
    ],
    visual: {
      surface: 'rgba(101, 98, 235, 0.22)',
      edge: '#5f59d4',
      glow: 'rgba(147, 197, 253, 0.45)',
    },
    gradient: 'linear-gradient(135deg, rgba(165, 94, 234, 0.18) 0%, rgba(69, 183, 209, 0.14) 55%, rgba(26, 35, 126, 0.14) 100%)',
    levelStart: 54,
    levelEnd: 63,
  },
];

export function getWorldLevelIndices(world: WorldDefinition, totalLevels: number): number[] {
  if (totalLevels <= 0) return [];
  const maxIndex = totalLevels - 1;
  if (world.levelStart > maxIndex) return [];
  if (world.levelEnd < 0) return [];

  const start = Math.max(0, world.levelStart);
  const end = Math.min(maxIndex, world.levelEnd);
  if (end < start) return [];
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

export function getWorldProgress(world: WorldDefinition, completedLevels: Set<number>, totalLevels: number) {
  const levelIndices = getWorldLevelIndices(world, totalLevels);
  const completedInWorld = levelIndices.filter((i) => completedLevels.has(i)).length;
  const totalInWorld = levelIndices.length;
  const ratio = totalInWorld > 0 ? completedInWorld / totalInWorld : 0;
  return { completedInWorld, totalInWorld, ratio, levelIndices };
}

export function getNextUncompletedLevel(world: WorldDefinition, completedLevels: Set<number>, totalLevels: number): number | null {
  const levelIndices = getWorldLevelIndices(world, totalLevels);
  for (const i of levelIndices) {
    if (!completedLevels.has(i)) return i;
  }
  return levelIndices.length ? levelIndices[levelIndices.length - 1] : null;
}

export function isWorldUnlocked(worldIndex: number, completedLevels: Set<number>, totalLevels: number): boolean {
  if (worldIndex <= 0) return true;
  const prev = WORLDS[worldIndex - 1];
  if (!prev) return true;
  const { levelIndices } = getWorldProgress(prev, completedLevels, totalLevels);
  // Require full completion of the previous world.
  return levelIndices.length > 0 && levelIndices.every((i) => completedLevels.has(i));
}

export function getWorldForLevel(levelIndex: number): WorldDefinition {
  const found = WORLDS.find((world) => levelIndex >= world.levelStart && levelIndex <= world.levelEnd);
  return found || WORLDS[0];
}

export function getWorldForId(worldId?: string): WorldDefinition | undefined {
  if (!worldId) return undefined;
  return WORLDS.find((world) => world.id === worldId);
}
